import React, {
    useState, useCallback, useMemo, memo,
} from 'react';

import { FileImageOutlined } from '@ant-design/icons';
import { message } from 'antd';
import PropTypes from 'prop-types';

import { wrapFormFieldArray } from '~/helpers/form-helper';
import { formatBytes } from '~/helpers/unit-helper';
import useClassNames from '~/hooks/use-classnames';

import ImgLoading from '../ImgLoading';
import Modal from '../Modal';
import styles from './style.module.scss';
import UploadItem from './UploadItem';

const transformSelectFile = file => {
    const url = URL.createObjectURL(file);
    let transformedImageFile;
    if (navigator.msSaveBlob) {
        // if in Microsoft Edge, then fake the "Blob" to
        // a "File" object.
        transformedImageFile = { ...file };
        transformedImageFile.lastModifiedDate = new Date();
        transformedImageFile.lastModified = Date.now();
        transformedImageFile.name = file.name;
    } else {
        transformedImageFile = new File([file], file.name, {
            type: file.type,
            lastModified: Date.now(),
            lastModifiedDate: new Date(),
        });
    }
    transformedImageFile.url = url;
    transformedImageFile.uid = url;

    return transformedImageFile;
};

const validateMaxSize = (file, maxSize) => {
    if (file.size > maxSize) {
        message.warn(`O arquivo ${file.name} excede o limite de ${formatBytes(maxSize)}`);
        return false;
    }
    return true;
};

const concatFileTypes = (type, acc, index) => {
    if (!acc) {
        return type;
    }
    return `${acc},${type}`;
};

const UploadFile = memo(({
    width, height,
    addIcon, className,
    addText, maxSize,
    fields, maxFiles,
    accept, meta,
}) => {
    const [fileToPreview, setFileToPreview] = useState(null);

    const { name, value: fieldValues } = fields;
    const {
        invalid, touched, error, submitError,
    } = meta;

    const hasError = invalid && touched;
    const errorText = error || submitError;
    const totalFiles = fieldValues?.length || 0;
    const showAddButton = totalFiles < maxFiles;

    const sizeStyle = useMemo(() => ({ width, height }), [width, height]);

    const acceptTypes = useMemo(() => {
        return Array.isArray(accept)
            ? accept.reduce(concatFileTypes, '')
            : accept;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, Array.isArray(accept) ? accept : [accept]);

    const containerClass = useClassNames([styles.container, className]);

    const handleClosePreview = useCallback(() => {
        setFileToPreview(null);
    }, []);

    const handlePreview = useCallback(file => {
        setFileToPreview(file);
    }, []);

    const handleRemove = useCallback(file => {
        const fileIndex = fieldValues.findIndex(f => f.uid === file.uid);
        if (fileIndex >= 0) {
            fields.remove(fileIndex);
        }
    }, [fields, fieldValues]);

    const handleDownload = useCallback(file => {
        window.open(file.url, '_blank');
    }, []);

    const handleAddFile = useCallback(event => {
        event.preventDefault();
        const { files } = event.target;

        const filesLeft = Math.max(maxFiles - totalFiles, 0);

        // é preciso usar o Array.prototype pois o objeto "files"
        // não é um Array, e sim um FileList
        const filteredFiles = Array.prototype.slice.call(files, 0, filesLeft);

        if (filteredFiles.length !== files.length) {
            message.warn(`Você atingiu o limite de ${maxFiles} arquivos enviados.`);
        }

        const mappedFiles = filteredFiles
            .filter(file => validateMaxSize(file, maxSize))
            .map(transformSelectFile);

        fields.push(mappedFiles[0]);

        // limpa o value do input para permitir selecionar
        // duas vezes o mesmo arquivo em seguida
        // eslint-disable-next-line no-param-reassign
        event.target.value = '';
    }, [maxFiles, totalFiles, fields, maxSize]);

    const renderItem = item => {
        return (
            <UploadItem
                key={item.uuid}
                file={item}
                width={width}
                height={height}
                onPreview={handlePreview}
                onRemove={handleRemove}
                onDownload={handleDownload}
            />
        );
    };

    return (
        <>
            <div className={containerClass}>
                {fieldValues?.map(renderItem)}
                {showAddButton ? (
                    <div className={styles.addButton} style={sizeStyle}>
                        <label
                            htmlFor={`upload-file-${name}`}
                            className={styles.addButtonContent}
                            style={sizeStyle}
                        >
                            {addIcon}
                            <div>{addText}</div>
                            <input
                                type="file"
                                id={`upload-file-${name}`}
                                onChange={handleAddFile}
                                className={styles.inputFile}
                                multiple={maxFiles > 1}
                                accept={acceptTypes}
                            />
                        </label>
                    </div>
                ) : null}
                {hasError ? (
                    <div className="ant-form-item-explain ant-form-item-explain-error">
                        {errorText}
                    </div>
                ) : null}
            </div>

            <Modal
                visible={Boolean(fileToPreview)}
                footer={null}
                onCancel={handleClosePreview}
            >
                {fileToPreview ? (
                    <ImgLoading
                        alt={`Pré-visualização do arquivo ${fileToPreview.name}`}
                        className={styles.imagePreview}
                        src={fileToPreview.url}
                    />
                ) : null}
            </Modal>
        </>
    );

});

UploadFile.propTypes = {
    height: PropTypes.number,
    width: PropTypes.number,
    addIcon: PropTypes.element,
    addText: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    className: PropTypes.string,
    maxSize: PropTypes.number,
    maxFiles: PropTypes.number,
    fields: PropTypes.shape({
        name: PropTypes.string.isRequired,
    }).isRequired,
    accept: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
};

UploadFile.defaultProps = {
    height: 100,
    width: 100,
    addIcon: <FileImageOutlined className={styles.addIcon} />,
    addText: 'Anexar',
    maxSize: 25 * 1024 * 1024, // 25 megas
    maxFiles: 30,
};

UploadFile.Field = wrapFormFieldArray(UploadFile);

export default UploadFile;
